import { useCallback } from 'react';
import { useTranslation, LOCALE_IDS } from '@parsina/framework/clinical/src/i18n/index';
import { lookupLocal, fillTemplate } from '../lib/brandText';

// ─────────────────────────────────────────────────────────────────────────────
// «منشی هوشمند» (PM-0517) — the strings of the doctor panel's secretary.
//
// App-local, like receptionStrings: they belong in the kit's catalogue as a
// `secretary` namespace once the doctor panel and the clinic panel share the
// surface, and `useSecretaryStrings` asks the kit FIRST, so that move is a
// deletion, never a behaviour change. Brand and credit tokens ({{brand}},
// {{coin}}) resolve as in the kit (../lib/brandText.js).
// ─────────────────────────────────────────────────────────────────────────────

import fa from './strings/fa.json';
import en from './strings/en.json';
import ar from './strings/ar.json';
import fr from './strings/fr.json';
import de from './strings/de.json';

const flatten = (obj, prefix = '', out = {}) => {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object') flatten(v, key, out); else out[key] = v;
  }
  return out;
};

const STRINGS = { fa: flatten(fa), en: flatten(en), ar: flatten(ar), fr: flatten(fr), de: flatten(de) };

/** Look a key up in the local table — exported for tests. */
export function secretaryString(locale, key, vars) {
  const { value, from } = lookupLocal(STRINGS, locale, key);
  return value == null ? key : fillTemplate(value, vars, from);
}

/** `t` for the secretary: the kit catalogue when it has the key, the local table otherwise. */
export function useSecretaryStrings() {
  const { t, has, locale } = useTranslation();
  return useCallback(
    (key, vars) => (has(key) ? t(key, vars) : secretaryString(locale, key, vars)),
    [t, has, locale],
  );
}

export const SECRETARY_STRING_LOCALES = [...LOCALE_IDS];
export const SECRETARY_STRING_KEYS = Object.keys(STRINGS.fa);
export const SECRETARY_STRING_TABLES = STRINGS;
